// services/ZIndexManager.js
const BASE_Z_INDEX = 2000; // Matches ZIndexManagerMain
const Z_INDEX_INCREMENT = 10;
const TOP_Z_INCREMENT = 1000; // Added for consistent focusing behavior

class ZIndexManager {
  constructor() {
    this._stickyStack = [];
    this._zIndices = new Map();
    //console.log('🎯 ZManager created for RIGHT panel, BASE_Z:', BASE_Z_INDEX);
  }

  initializeStickies(stickies) {
    // Skip if already initialized to prevent duplicate z-index assignments
    if (this._stickyStack.length > 0) {
      //console.warn("⚠️ !!Stickies already initialized in RIGHT panel. Skipping!!!!!!!!!!!!...");
      return;
    }
    
    //console.log("🛠 Initializing stickies in RIGHT panel with count:", stickies.length);
    
    // Map stickies to their IDs and set initial z-indices
    this._stickyStack = stickies.map((sticky, index) => {
      // Use existing z-index if available, otherwise calculate new one
      const zIndex = sticky.zIndex ?? (BASE_Z_INDEX + ((index + 1) * Z_INDEX_INCREMENT));
      this._zIndices.set(sticky.id, zIndex);
      
      return sticky.id;
    });
    
    // Re-index all stickies to ensure consistent ordering
    this._reindexAll();
  }

  bringToFront(stickyId) {
    if (!this._stickyStack.includes(stickyId)) {
      return BASE_Z_INDEX;
    }
    
    // Get current z-indices and find the highest
    const existingIndices = new Set(this._zIndices.values());
    const highest = Math.max(...existingIndices, BASE_Z_INDEX);
    
    // Calculate new z-index with a significant increment to ensure it's on top
    let newZ = highest + TOP_Z_INCREMENT;
    
    // Ensure uniqueness
    while (existingIndices.has(newZ)) {
      newZ += TOP_Z_INCREMENT;
    }
    
    // Set the new z-index and update the sticky stack
    this._zIndices.set(stickyId, newZ);
    this._stickyStack = [
      ...this._stickyStack.filter(id => id !== stickyId),
      stickyId
    ];
    
    return newZ;
  }

  addSticky(stickyId) {
    if (!this._stickyStack.includes(stickyId)) {
      // Get the new top z-index first
      const newZIndex = this._getTopZIndex() + Z_INDEX_INCREMENT;
      console.log(`[ZManager] Adding sticky ${stickyId} at zIndex ${newZIndex}`);

      
      // Add to stack
      this._stickyStack.push(stickyId);
      
      // Set the new z-index directly instead of reindexing
      this._zIndices.set(stickyId, newZIndex);
      
      return newZIndex;
    }
    return this._zIndices.get(stickyId);
  }

  removeSticky(stickyId) {
    this._stickyStack = this._stickyStack.filter(id => id !== stickyId);
    this._zIndices.delete(stickyId);
    this._reindexAll();
  }

  getStickyZIndex(stickyId) {
    return this._zIndices.get(stickyId) || BASE_Z_INDEX;
  }

  getAllZIndices() {
    return Object.fromEntries(this._zIndices);
  }

  _getTopZIndex() {
    const highest = Array.from(this._zIndices.values())
      .filter(z => typeof z === 'number')
      .reduce((max, z) => Math.max(max, z), BASE_Z_INDEX);
    
    return highest;
  }

  _reindexAll() {
    // Save the current top z-index before reindexing
    const currentTop = this._getTopZIndex();
    const oldIndices = new Map(this._zIndices);
    
    this._zIndices.clear();
    this._stickyStack.forEach((id, index) => {
      // If it was on top, keep it on top
      if (oldIndices.get(id) === currentTop) {
        this._zIndices.set(id, currentTop);
      } else {
        // Use doubled spacing for better separation between layers
        const zIndex = BASE_Z_INDEX + ((index + 1) * Z_INDEX_INCREMENT * 2);
        this._zIndices.set(id, zIndex);
      }
    });
  }
}

export default ZIndexManager;